import { useCallback } from 'react';

import { addBusinessDays, expectedPayoffDate } from '../lib/termDates';

export { addBusinessDays };

/**
 * Custom hook for deal form field calculations
 */
export const useDealFormCalculations = (merchants, setFormData) => {
  /**
   * Handle form field changes with auto-calculations
   */
  const handleFieldChange = useCallback((field, value) => {
    // Fast path: just update the field for text inputs that don't need calculations
    const fieldsNeedingCalculation = [
      'company_id', 
      'funding_amount', 
      'rate', 
      'fees', 
      'refi_balance_payoff', 
      'term_days', 
      'funding_date'
    ];
    
    if (!fieldsNeedingCalculation.includes(field)) {
      setFormData(prev => ({ ...prev, [field]: value }));
      return;
    }
    
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-set shortcode and name when merchant is selected
      if (field === 'company_id') {
        const merchant = merchants.find(m => m.id === value);
        if (merchant) {
          if (!prev.deal_shortcode || prev.deal_shortcode === '') {
            updated.deal_shortcode = merchant.shortcode || '';
          }
          if (!prev.deal_name || prev.deal_name === '') {
            updated.deal_name = merchant.company_name || '';
          }
          updated.legal_business_name = merchant.company_name || '';
          updated.business_address = merchant.business_address || '';
          updated.owner_name = merchant.owner_name || merchant.name || '';
          updated.owner_phone = merchant.phone || '';
          updated.owner_email = merchant.email || '';
        }
      }
      
      // Calculate payback when funding_amount or rate changes
      if (field === 'funding_amount' || field === 'rate') {
        const fundingAmt = parseFloat(field === 'funding_amount' ? value : prev.funding_amount) || 0;
        const rate = parseFloat(field === 'rate' ? value : prev.rate) || 0;
        if (fundingAmt > 0 && rate > 0) {
          const newPayback = (fundingAmt * rate).toFixed(2);
          updated.payback = newPayback;
          const term = parseFloat(prev.term_days) || 0;
          if (term > 0) {
            updated.payment_amount = (parseFloat(newPayback) / term).toFixed(2);
          }
        }
        // Also update deposit amount
        const fees = parseFloat(prev.fees) || 0;
        const refiPayoff = parseFloat(prev.refi_balance_payoff) || 0;
        const fundingForDeposit = parseFloat(field === 'funding_amount' ? value : prev.funding_amount) || 0;
        updated.deposit_amount = (fundingForDeposit - fees - refiPayoff).toFixed(2);
      }
      
      // Auto-calculate deposit amount (Net to Merchant) when fees or refi_balance_payoff changes
      if (field === 'fees' || field === 'refi_balance_payoff') {
        const fundingAmt = parseFloat(prev.funding_amount) || 0;
        const fees = parseFloat(field === 'fees' ? value : prev.fees) || 0;
        const refiPayoff = parseFloat(field === 'refi_balance_payoff' ? value : prev.refi_balance_payoff) || 0;
        updated.deposit_amount = (fundingAmt - fees - refiPayoff).toFixed(2);
      }
      
      // Auto-calculate payment amount and estimated end date when term changes
      if (field === 'term_days') {
        const payback = parseFloat(prev.payback) || 0;
        const term = parseFloat(value) || 0;
        if (payback > 0 && term > 0) {
          updated.payment_amount = (payback / term).toFixed(2);
        }
        const fundingDate = prev.funding_date;
        if (fundingDate && term > 0) {
          updated.estimated_end_date = expectedPayoffDate(fundingDate, term, prev.payment_frequency);
        }
      }

      // Auto-calculate estimated end date when funding date or payment frequency changes
      // (term is in the frequency's unit — business days / weeks / bi-weekly / months, Josh 10/01)
      if (field === 'funding_date' || field === 'payment_frequency') {
        const term = parseFloat(prev.term_days) || 0;
        const fundingDate = field === 'funding_date' ? value : prev.funding_date;
        const frequency = field === 'payment_frequency' ? value : prev.payment_frequency;
        if (fundingDate && term > 0) {
          updated.estimated_end_date = expectedPayoffDate(fundingDate, term, frequency);
        }
      }
      
      return updated;
    });
  }, [merchants, setFormData]);

  /**
   * Prepare payload for submission
   */
  const preparePayload = useCallback((formData, isEdit = false) => {
    const payload = {
      ...formData,
      deal_id: parseInt(formData.deal_id),
      funding_amount: parseFloat(formData.funding_amount) || 0,
      fees: parseFloat(formData.fees) || 0,
      refi_balance_payoff: parseFloat(formData.refi_balance_payoff) || 0,
      deposit_amount: parseFloat(formData.deposit_amount) || 0,
      rate: parseFloat(formData.rate) || 0,
      term_days: parseInt(formData.term_days) || 0,
      payback: parseFloat(formData.payback) || 0,
      payment_amount: parseFloat(formData.payment_amount) || 0,
      funder_percentage: parseFloat(formData.funder_percentage) || 100,
      broker_commission: parseFloat(formData.broker_commission) || 0,
      commission_rate: parseFloat(formData.commission_rate) || 0,
      years_in_business: parseFloat(formData.years_in_business) || 0,
      monthly_revenue: parseFloat(formData.monthly_revenue) || 0,
      funding_date: formData.funding_date || new Date().toISOString().split('T')[0]
    };

    if (!isEdit) {
      payload.payments_made = 0;
      payload.balance = payload.payback;
    }

    // Strip empty strings to null for optional date/string fields
    for (const key of ['owner_dob', 'estimated_end_date', 'owner_ssn', 'ein_tax_id', 'owner_address', 'owner_phone', 'owner_email', 'business_address', 'legal_business_name', 'business_type', 'notes']) {
      if (payload[key] === '') payload[key] = null;
    }

    return payload;
  }, []);

  return {
    handleFieldChange,
    preparePayload,
    addBusinessDays,
  };
};

export default useDealFormCalculations;
